import { Box, Chip, Container, Paper, Typography } from '@mui/material';
import { FC, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMuiModal } from '../../_core/hooks';
import { DistrictDto, EngagementStatusDto, FieldOptionInterface, ProviderDto } from '@families-link/shared';
import { LanguagePieChart } from '../components/language-pie-chart/language-pie-chart.component';
import {
	useGetActiveEngagementStatisticLinguistLinkQuery,
	useGetEngagementsStatisticQuery,
	useGetEngagementsStatusesSummaryQuery,
	useGetEngagementStatisticLinguistLinkQuery,
	useGetEngagementStatusesLinguistLinkQuery,
	useGetLanguageDistributionLinguistLinkQuery,
	useGetLanguageDistributionQuery,
} from '../../../store/api/engagements.api';
import { ChipWithoutBorderCloseIcon } from '../../_core/components/icons';
import { chipFilterStyles } from '../../_core/styles';
import {
	AppointmentStats,
	EngagStats,
	FilterIcon,
	ProjectStats,
	Providerstats,
} from '../../_core/constants/icons.constants';
import { EngagementsStatusesPieChart } from '../components/engagement-status-pie-chart/engagement-status-pie-chart.component';
import { EngagementFullDateChart } from '../components/engagement-full-data-chart/engagement-full-data-chart.component';
import { ProviderFullDateChart } from '../components/provider-full-data-chart/provider-full-data-chart.component';
import dayjs from 'dayjs';
import { CoreButton, CoreIconButton } from '../../_core/components/_ui/core-button';
import {
	DashboardFiltersModal,
	DashboardFiltersModalProps,
} from '../components/filters-modal-form/filters-modal-form.component';
import { format } from 'date-fns';
import { FilterIconComponent } from '../../_core/components/_ui/core-autocomplete/filter-icon.component';
import { useDashboardFilters } from '../hooks/use-dashboard-filters.hook';
import { inverseTransformFilterValues } from '../utils/inverseTransformFilterValues.util';
import { CoreLoadingWall } from '../../_core/components/_ui/core-loading-wall';
import { FadeSpinner } from '../../_core/components/_ui/fade-spinner';

export type FilterValuesArrays = {
	districts: DistrictDto[];
	engagementType: string[];
	duration: Date[] | null[];
	schools: FieldOptionInterface[];
	programs: FieldOptionInterface[];
	services: FieldOptionInterface[];
	providers: ProviderDto[];
	languages: string[];
	engagementStatus: EngagementStatusDto[];
	//linkType: string;
};

export type FilterValuesStrings = {
	districtIds: string;
	engagementTypes: string;
	duration: (Date | string | null)[];
	schoolIds: string;
	programIds: string;
	serviceIds: string;
	providerIds: string;
	languages: string;
	engagementStatuses: string;
	//linkType: string;
};

export type DashboardPageProps = {
	isAdminRoute: boolean;
	currentDistrictId?: string | null;
	currentDistrict?: DistrictDto;
	transformedFilterValues?: FilterValuesStrings;
	isReadOnly?: boolean;
	isPublic?: boolean;
};

export const DashboardPage: FC<DashboardPageProps> = (props) => {
	const {
		isAdminRoute,
		currentDistrictId,
		currentDistrict,
		transformedFilterValues: externalTransformedFilters,
		isReadOnly = false,
		isPublic = false,
	} = props;

	const [t] = useTranslation();

	const today = new Date();
	const last30Days = new Date();
	last30Days.setDate(today.getDate() - 30);

	const initialFilterValues = {
		districts: currentDistrict ? [currentDistrict] : [],
		engagementType: [],
		duration: [last30Days, today],
		schools: [],
		programs: [],
		services: [],
		providers: [],
		languages: [],
		engagementStatus: [],
		//linkType: 'family-link',
	};

	const initialTransformedFilterValues = {
		districtIds: currentDistrictId ?? '',
		engagementTypes: '',
		duration: [last30Days, today],
		schoolIds: '',
		programIds: '',
		serviceIds: '',
		providerIds: '',
		languages: '',
		engagementStatuses: '',
		//linkType: 'family-link',
	};

	const [filterValues, setFilterValues] = useState<FilterValuesArrays>(() => {
		if (externalTransformedFilters) {
			return inverseTransformFilterValues(externalTransformedFilters, currentDistrict);
		}
		return initialFilterValues;
	});

	const [transformedFilterValues, setTransformedFilterValues] = useState<FilterValuesStrings>(
		externalTransformedFilters ?? initialTransformedFilterValues
	);

	const isLinguistLink = !!currentDistrict?.districtLinguistLinkId;

	const { setDashboardFilters } = useDashboardFilters();

	useEffect(() => {
		if (currentDistrict) {
			setFilterValues((prevState) => ({ ...prevState, districts: [currentDistrict] }));
		}
	}, [currentDistrict]);

	useEffect(() => {
		const transformed = transformFilterValues(filterValues);

		const transformedWithStrings = {
			...transformed,
			duration: transformed.duration.map((d) => (d instanceof Date ? d.toISOString() : '')),
			//linkType: filterValues.linkType,
		};
		setTransformedFilterValues(transformFilterValues(filterValues));
		setDashboardFilters(transformedWithStrings);
	}, [filterValues]);

	const { openModal, closeModal } = useMuiModal<DashboardFiltersModalProps>(DashboardFiltersModal);

	const handleOpenFiltersModal = useCallback(() => {
		openModal({
			isAdminRoute: isAdminRoute,
			currentDistrict: currentDistrict,
			defaultValues: filterValues,
			submitAction: setFilterValues,
			closeModal: closeModal,
		});
	}, [openModal, filterValues]);

	const handleDeleteChip = (fieldName: string, id?: string) => {
		switch (fieldName) {
			case 'duration':
				setFilterValues((prevValue) => ({ ...prevValue, duration: [null, null] }));
				break;
			case 'engagementType':
				setFilterValues((prevValue) => ({
					...prevValue,
					engagementType: prevValue.engagementType.filter((engagementType: string) => engagementType !== id),
				}));
				break;
			case 'districts':
				setFilterValues((prevValue) => ({
					...prevValue,
					districts: prevValue.districts.filter((district: DistrictDto) => district._id !== id),
				}));
				break;
			case 'schools':
				setFilterValues((prevValue) => ({
					...prevValue,
					schools: prevValue.schools.filter((school: FieldOptionInterface) => school._id !== id),
				}));
				break;
			case 'services':
				setFilterValues((prevValue) => ({
					...prevValue,
					services: prevValue.services.filter((service: FieldOptionInterface) => service._id !== id),
				}));
				break;
			case 'providers':
				setFilterValues((prevValue) => ({
					...prevValue,
					providers: prevValue.providers.filter((provider: ProviderDto) => provider._id !== id),
				}));
				break;
			case 'languages':
				setFilterValues((prevValue) => ({
					...prevValue,
					languages: prevValue.languages.filter((language: string) => language !== id),
				}));
				break;
			case 'programs':
				setFilterValues((prevValue) => ({
					...prevValue,
					programs: prevValue.programs.filter((program: FieldOptionInterface) => program._id !== id),
				}));
				break;
			case 'engagementStatus':
				setFilterValues((prevValue) => ({
					...prevValue,
					engagementStatus: prevValue.engagementStatus.filter(
						(engagementStatus: EngagementStatusDto) => engagementStatus._id !== id
					),
				}));
				break;
			/*case 'linkType':
				setFilterValues((prevValue) => ({
					...prevValue,
					linkType: id || 'family-link',
				}));
				break;*/
			default:
				break;
		}
	};

	const linguistArgs = {
		start:
			transformedFilterValues?.duration?.length && transformedFilterValues.duration[0]
				? dayjs(filterValues.duration[0]).startOf('day').format('YYYY-MM-DDTHH:mm:ss.SSSZ')
				: '',
		end:
			transformedFilterValues?.duration?.length && transformedFilterValues.duration[1]
				? dayjs(filterValues.duration[1]).endOf('day').format('YYYY-MM-DDTHH:mm:ss.SSSZ')
				: '',
		district: currentDistrictId ?? transformedFilterValues?.districtIds,
		languages: transformedFilterValues?.languages,
		engagementTypes: transformedFilterValues?.engagementTypes,
		engagementStatuses: transformedFilterValues?.engagementStatuses,
	};

	const normalArgs = {
		start:
			transformedFilterValues?.duration?.length && transformedFilterValues.duration[0]
				? dayjs(filterValues.duration[0]).startOf('day').format('YYYY-MM-DDTHH:mm:ss.SSSZ')
				: '',
		end:
			transformedFilterValues?.duration?.length && transformedFilterValues.duration[1]
				? dayjs(filterValues.duration[1]).endOf('day').format('YYYY-MM-DDTHH:mm:ss.SSSZ')
				: '',
		schools: transformedFilterValues?.schoolIds,
		district: currentDistrictId,
		districts: transformedFilterValues?.districtIds,
		services: transformedFilterValues?.serviceIds,
		programs: transformedFilterValues?.programIds,
		providers: transformedFilterValues?.providerIds,
		languages: transformedFilterValues?.languages,
		engagementStatuses: transformedFilterValues?.engagementStatuses,
		engagementTypes: transformedFilterValues?.engagementTypes,
	};

	/*const languageLinguistQuery = useGetLanguageDistributionLinguistLinkQuery(linguistArgs, {
		refetchOnMountOrArgChange: true,
		skip: !isLinguistLink,
	});*/

	const languageNormalQuery = useGetLanguageDistributionQuery(normalArgs, {
		refetchOnMountOrArgChange: true,
		//skip: isLinguistLink,
	});

	const languageData = /*isLinguistLink ? languageLinguistQuery.data : */ languageNormalQuery.data;
	const isFetchingLanguagePieChart =
		/*isLinguistLink ? languageLinguistQuery.isFetching :*/ languageNormalQuery.isFetching;

	/*const statisticLinguistQuery = useGetEngagementStatisticLinguistLinkQuery(linguistArgs, {
		refetchOnMountOrArgChange: true,
		skip: !isLinguistLink,
	});*/

	const statisticNormalQuery = useGetEngagementsStatisticQuery(normalArgs, {
		refetchOnMountOrArgChange: true,
		//skip: isLinguistLink,
	});

	const engagementsStats = /* isLinguistLink ? statisticLinguistQuery.data : */ statisticNormalQuery.data;
	const isFetching = /* isLinguistLink ? statisticLinguistQuery.isFetching :*/ statisticNormalQuery.isFetching;

	/*const statusesLinguistQuery = useGetEngagementStatusesLinguistLinkQuery(linguistArgs, {
		refetchOnMountOrArgChange: true,
		skip: !isLinguistLink,
	});*/

	const statusesNormalQuery = useGetEngagementsStatusesSummaryQuery(normalArgs, {
		refetchOnMountOrArgChange: true,
	});

	const engagementsStatusData = /* isLinguistLink ? statusesLinguistQuery.data : */ statusesNormalQuery.data;
	const isFetchingStatusPieChart =
		/*isLinguistLink ? statusesLinguistQuery.isFetching : */ statusesNormalQuery.isFetching;

	return (
		<Box sx={{ display: 'flex', flexDirection: 'column', width: '100%', height: '100%', gap: 1, overflowY: 'auto' }}>
			<Box sx={{ display: 'flex', width: '100%', alignItems: 'center', gap: 1 }}>
				<Paper
					elevation={0}
					sx={{
						width: '100%',
						display: 'flex',
						borderRadius: '16px',
						minHeight: '56px',
						px: '12px',
						py: 1,
						gap: 2,
						alignItems: 'center',
					}}
				>
					{!isPublic && (
						<Box
							sx={{
								minWidth: '90%',
								maxWidth: 'calc(100%-38px)',
								height: '100%',
								display: 'flex',
								alignItems: 'center',
								gap: 1,
								flexWrap: 'wrap',
							}}
						>
							{filterValues.duration?.length && filterValues.duration[0] && filterValues.duration[1] ? (
								<Chip
									sx={chipFilterStyles}
									variant="outlined"
									label={`${format(filterValues.duration[0], 'MM.dd.yyyy')} - ${format(
										filterValues.duration[1],
										'MM.dd.yyyy'
									)}`}
									deleteIcon={<ChipWithoutBorderCloseIcon />}
									onDelete={() => {
										handleDeleteChip('duration');
									}}
								/>
							) : null}
							{filterValues.engagementType?.length
								? filterValues.engagementType.map((engagementType: string) => (
										<Chip
											sx={chipFilterStyles}
											variant="outlined"
											label={engagementType}
											deleteIcon={<ChipWithoutBorderCloseIcon />}
											onDelete={() => {
												handleDeleteChip('engagementType', engagementType);
											}}
										/>
								  ))
								: null}
							{isAdminRoute
								? filterValues.districts?.length
									? filterValues.districts.map((district: DistrictDto) => (
											<Chip
												sx={chipFilterStyles}
												variant="outlined"
												label={district.name}
												deleteIcon={<ChipWithoutBorderCloseIcon />}
												onDelete={() => {
													handleDeleteChip('districts', district._id);
												}}
											/>
									  ))
									: null
								: null}

							{filterValues.schools?.length
								? filterValues.schools.map((school: FieldOptionInterface) => (
										<Chip
											sx={chipFilterStyles}
											variant="outlined"
											label={school.text}
											deleteIcon={<ChipWithoutBorderCloseIcon />}
											onDelete={() => {
												handleDeleteChip('schools', school._id?.toString());
											}}
										/>
								  ))
								: null}
							{filterValues.services?.length
								? filterValues.services.map((service: FieldOptionInterface) => (
										<Chip
											sx={chipFilterStyles}
											variant="outlined"
											label={service.text}
											deleteIcon={<ChipWithoutBorderCloseIcon />}
											onDelete={() => {
												handleDeleteChip('services', service._id?.toString());
											}}
										/>
								  ))
								: null}
							{filterValues.providers?.length
								? filterValues.providers.map((provider: ProviderDto) => (
										<Chip
											sx={chipFilterStyles}
											variant="outlined"
											label={provider.fullName}
											deleteIcon={<ChipWithoutBorderCloseIcon />}
											onDelete={() => {
												handleDeleteChip('providers', provider._id);
											}}
										/>
								  ))
								: null}
							{filterValues.languages?.length
								? filterValues.languages.map((language: string) => (
										<Chip
											sx={chipFilterStyles}
											variant="outlined"
											label={language}
											deleteIcon={<ChipWithoutBorderCloseIcon />}
											onDelete={() => {
												handleDeleteChip('languages', language);
											}}
										/>
								  ))
								: null}
							{filterValues.programs?.length
								? filterValues.programs.map((program: FieldOptionInterface) => (
										<Chip
											sx={chipFilterStyles}
											variant="outlined"
											label={program.text}
											deleteIcon={<ChipWithoutBorderCloseIcon />}
											onDelete={() => {
												handleDeleteChip('programs', program._id?.toString());
											}}
										/>
								  ))
								: null}
							{filterValues.engagementStatus?.length
								? filterValues.engagementStatus.map((engagementStatus: EngagementStatusDto) => (
										<Chip
											sx={chipFilterStyles}
											variant="outlined"
											label={engagementStatus.statusName}
											deleteIcon={<ChipWithoutBorderCloseIcon />}
											onDelete={() => {
												handleDeleteChip('engagementStatus', engagementStatus._id);
											}}
										/>
								  ))
								: null}

							{/*filterValues.linkType && (
								<Chip
									sx={chipFilterStyles}
									variant="outlined"
									label={filterValues.linkType === 'family-link' ? 'Family Link' : 'Linguist Link'}
									deleteIcon={<ChipWithoutBorderCloseIcon />}
									onDelete={() => handleDeleteChip('linkType', filterValues.linkType)}
								/>
							)*/}
						</Box>
					)}
					{!isPublic && (
						<CoreButton
							variant="info-light"
							contraModeStyleButton
							onClick={handleOpenFiltersModal}
							sx={{ ml: 'auto', borderRadius: '12px', p: 1 }}
						>
							<FilterIconComponent sx={{ stroke: 'white' }} />
						</CoreButton>
					)}
					{isPublic && (
						<Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
							<Typography sx={{ fontSize: '24px', fontWeight: 500, px: 2, py: 1 }}> {t('nav.dashboard')}</Typography>
							{filterValues.duration?.[0] && filterValues.duration?.[1] && (
								<Typography sx={{ fontSize: '16px', fontWeight: 500, color: (theme) => theme.colors.grayLightBlue }}>
									{`${format(filterValues.duration[0], 'MM.dd.yyyy')} - ${format(
										filterValues.duration[1],
										'MM.dd.yyyy'
									)}`}
								</Typography>
							)}
						</Box>
					)}
				</Paper>
			</Box>

			<Box
				sx={{
					display: 'flex',
					width: '100%',
					height: '280px',
					gap: 1,
				}}
			>
				<Box
					sx={{
						display: 'flex',
						flexDirection: 'column',
						gap: 1,
						width: '100%',
						height: '100%',
					}}
				>
					<Box sx={{ display: 'flex', gap: 1, flex: 1 }}>
						<Paper elevation={0} sx={{ width: 'calc(50% - 4px)', borderRadius: '16px', height: '136px' }}>
							{isFetching ? (
								<Box
									sx={{
										height: '100%',
										display: 'flex',
										alignItems: 'center',
										justifyContent: 'center',
										width: '100%',
									}}
								>
									<FadeSpinner size={51} height={15} width={6} borderRadius={3} />
								</Box>
							) : (
								<Box
									sx={{
										display: 'flex',
										flexDirection: 'column',
										gap: 2,
										width: '100%',
										px: 3,

										height: '100%',
										justifyContent: 'center',
									}}
								>
									<Typography sx={{ color: (theme) => theme.colors.grayLightBlue, fontSize: '16px', fontWeight: 500 }}>
										Engagements
									</Typography>
									<Box
										sx={{
											display: 'flex',
											flexDirection: 'row',
											gap: 1,
											alignItems: 'center',
											justifyContent: 'center',
										}}
									>
										<EngagStats />
										<Typography
											sx={{
												color: (theme) => theme.colors.lightBlue,
												fontSize: '56px',
												lineHeight: '56px',
												fontWeight: 500,
											}}
										>
											{engagementsStats && engagementsStats?.totalEngagements > 1000
												? `${(engagementsStats?.totalEngagements / 1000).toFixed(1)}k`
												: engagementsStats?.totalEngagements}
										</Typography>
									</Box>
								</Box>
							)}
						</Paper>
						<Paper elevation={0} sx={{ width: 'calc(50% - 4px)', borderRadius: '16px', height: '136px' }}>
							{isFetching ? (
								<Box
									sx={{
										height: '100%',
										display: 'flex',
										alignItems: 'center',
										justifyContent: 'center',
										width: '100%',
									}}
								>
									<FadeSpinner size={51} height={15} width={6} borderRadius={3} />
								</Box>
							) : (
								<Box
									sx={{
										display: 'flex',
										flexDirection: 'column',
										gap: 2,
										width: '100%',
										px: 3,

										height: '100%',
										justifyContent: 'center',
									}}
								>
									<Typography sx={{ color: (theme) => theme.colors.grayLightBlue, fontSize: '16px', fontWeight: 500 }}>
										Providers
									</Typography>
									<Box
										sx={{
											display: 'flex',
											flexDirection: 'row',
											gap: 1,
											alignItems: 'center',
											justifyContent: 'center',
										}}
									>
										<Providerstats />
										<Typography
											sx={{
												color: (theme) => theme.colors.lightBlue,
												fontSize: '56px',
												lineHeight: '56px',
												fontWeight: 500,
											}}
										>
											{engagementsStats && engagementsStats?.uniqueProviders > 1000
												? `${(engagementsStats?.uniqueProviders / 1000).toFixed(1)}k`
												: engagementsStats?.uniqueProviders}
										</Typography>
									</Box>
								</Box>
							)}
						</Paper>
					</Box>
					<Box sx={{ display: 'flex', gap: 1, flex: 1 }}>
						<Paper elevation={0} sx={{ width: 'calc(50% - 4px)', borderRadius: '16px', height: '136px' }}>
							{isFetching ? (
								<Box
									sx={{
										height: '100%',
										display: 'flex',
										alignItems: 'center',
										justifyContent: 'center',
										width: '100%',
									}}
								>
									<FadeSpinner size={51} height={15} width={6} borderRadius={3} />
								</Box>
							) : (
								<Box
									sx={{
										display: 'flex',
										flexDirection: 'column',
										gap: 2,
										width: '100%',
										px: 3,
										height: '100%',
										justifyContent: 'center',
									}}
								>
									<Typography sx={{ color: (theme) => theme.colors.grayLightBlue, fontSize: '16px', fontWeight: 500 }}>
										Projects
									</Typography>
									<Box
										sx={{
											display: 'flex',
											flexDirection: 'row',
											gap: 1,
											alignItems: 'center',
											justifyContent: 'center',
										}}
									>
										<ProjectStats />
										<Typography sx={{ color: '#F89377', fontSize: '56px', lineHeight: '56px', fontWeight: 500 }}>
											{engagementsStats && engagementsStats?.projectEngagements > 1000
												? `${(engagementsStats?.projectEngagements / 1000).toFixed(1)}k`
												: engagementsStats?.projectEngagements}
										</Typography>
									</Box>
								</Box>
							)}
						</Paper>
						<Paper elevation={0} sx={{ width: 'calc(50% - 4px)', borderRadius: '16px', height: '136px' }}>
							{isFetching ? (
								<Box
									sx={{
										height: '100%',
										display: 'flex',
										alignItems: 'center',
										justifyContent: 'center',
										width: '100%',
									}}
								>
									<FadeSpinner size={51} height={15} width={6} borderRadius={3} />
								</Box>
							) : (
								<Box
									sx={{
										display: 'flex',
										flexDirection: 'column',
										gap: 2,
										width: '100%',
										px: 3,
										height: '100%',
										justifyContent: 'center',
									}}
								>
									<Typography sx={{ color: (theme) => theme.colors.grayLightBlue, fontSize: '16px', fontWeight: 500 }}>
										Appointments
									</Typography>
									<Box
										sx={{
											display: 'flex',
											flexDirection: 'row',
											gap: 1,
											alignItems: 'center',
											justifyContent: 'center',
										}}
									>
										<AppointmentStats />
										<Typography sx={{ color: '#F89377', fontSize: '56px', lineHeight: '56px', fontWeight: 500 }}>
											{engagementsStats && engagementsStats?.appointmentEngagements > 1000
												? `${(engagementsStats?.appointmentEngagements / 1000).toFixed(1)}k`
												: engagementsStats?.appointmentEngagements}
										</Typography>
									</Box>
								</Box>
							)}
						</Paper>
					</Box>
				</Box>

				<Paper
					elevation={0}
					sx={{
						width: '100%',
						height: '100%',
						borderRadius: '16px',
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'center',
					}}
				>
					<Box sx={{ height: '100%', flex: 1, py: 2, px: 3 }}>
						<EngagementsStatusesPieChart
							data={engagementsStatusData ? engagementsStatusData : []}
							loading={isFetchingStatusPieChart}
						/>
					</Box>
				</Paper>

				<Paper
					elevation={0}
					sx={{
						height: '100%',
						width: '100%',
						borderRadius: '16px',
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'center',
					}}
				>
					<Box sx={{ height: '100%', flex: 1, py: 2, px: 3 }}>
						<LanguagePieChart data={languageData} loading={isFetchingLanguagePieChart} />
					</Box>
				</Paper>
			</Box>

			<Box sx={{ display: 'flex', flexDirection: 'row', width: '100%', gap: 1, minHeight: '270px', flex: 1 }}>
				<Paper
					elevation={0}
					sx={{ width: '45%', display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}
				>
					<Box
						sx={{
							p: 3,
							pb: 2,
							width: '100%',
							height: '100%',
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'center',
						}}
					>
						<ProviderFullDateChart
							currentDistrictId={currentDistrictId}
							transformedFilterValues={transformedFilterValues}
							isLinguistLink={isLinguistLink}
						/>
					</Box>
				</Paper>
				<Paper
					elevation={0}
					sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}
				>
					<Box
						sx={{
							p: 3,
							pb: 2,
							width: '100%',
							height: '100%',
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'center',
						}}
					>
						<EngagementFullDateChart
							currentDistrictId={currentDistrictId}
							transformedFilterValues={transformedFilterValues}
							isLinguistLink={isLinguistLink}
						/>
					</Box>
				</Paper>
			</Box>
		</Box>
	);
};

const transformFilterValues = (values: FilterValuesArrays): FilterValuesStrings => ({
	duration: values.duration,
	districtIds: values.districts?.length ? values.districts.map((district: DistrictDto) => district._id).join(',') : '',
	engagementTypes: values.engagementType?.length ? values.engagementType.join(',') : '',
	schoolIds: values.schools?.length ? values.schools.map((school: FieldOptionInterface) => school._id).join(',') : '',
	serviceIds: values.services?.length
		? values.services.map((service: FieldOptionInterface) => service._id).join(',')
		: '',
	programIds: values.programs?.length
		? values.programs.map((program: FieldOptionInterface) => program._id).join(',')
		: '',
	languages: values.languages?.length ? values.languages.join(',') : '',
	providerIds: values.providers?.length ? values.providers.map((provider: ProviderDto) => provider._id).join(',') : '',
	engagementStatuses: values.engagementStatus?.length
		? values.engagementStatus.map((engagementStatus: EngagementStatusDto) => engagementStatus._id).join(',')
		: '',
	//linkType: values.linkType ?? 'family-link',
});
